import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { UserProfileModal } from "./UserProfileModal";
import { useHeaderUnreadCount } from "@/hooks/useHeaderUnreadCount";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface UserProfileTriggerProps {
  setIsMenuOpen?: (value: boolean) => void;
  variant?: "sidebar" | "header";
  isCollapsed?: boolean;
}

/**
 * Trigger button for the UserProfileModal
 * Can be used in sidebar or header with different styles
 */
export function UserProfileTrigger({
  setIsMenuOpen,
  variant = "sidebar",
  isCollapsed = false,
}: Readonly<UserProfileTriggerProps>) {
  const { user } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEverOpened, setModalEverOpened] = useState(false);

  const unreadCount = useHeaderUnreadCount();

  const handleClick = () => {
    setModalEverOpened(true);
    setModalOpen(true);
    if (setIsMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  // Get user initials for avatar fallback
  const getInitials = () => {
    const email = user?.email || "";
    const name = user?.user_metadata?.full_name || email;
    if (name.includes(" ")) {
      const parts = name.split(" ");
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (variant === "sidebar") {
    return (
      <>
        <div className="p-3">
          <Button
            variant="ghost"
            className={cn(
              "flex w-full items-center gap-3 p-3 rounded-lg transition-all hover:bg-muted",
              isCollapsed && "justify-center p-2"
            )}
            onClick={handleClick}
          >
            <div className="relative flex-shrink-0">
              <Avatar className={cn(isCollapsed ? "h-8 w-8" : "h-10 w-10")}>
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-sm font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              {/* Notification badge on avatar */}
              {unreadCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center border-2 border-background"
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </Badge>
              )}
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-semibold truncate text-foreground">
                  {user?.user_metadata?.full_name || user?.user_metadata?.display_name || "User"}
                </p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            )}
            {!isCollapsed && (
              <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
          </Button>
        </div>
        {modalEverOpened && <UserProfileModal open={modalOpen} onOpenChange={setModalOpen} />}
      </>
    );
  }

  // Header variant (can be used in top navigation)
  return (
    <>
      <Button
        variant="ghost"
        aria-label="Open user profile and account menu"
        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 h-8 sm:h-10 rounded-lg hover:bg-primary/10 hover:shadow-sm transition-all duration-300 relative"
        onClick={handleClick}
      >
        <div className="relative">
          <Avatar className="h-7 w-7 sm:h-8 sm:w-8">
            <AvatarImage src={user?.user_metadata?.avatar_url} />
            <AvatarFallback className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-xs sm:text-sm font-semibold">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
          {/* Notification badge on avatar */}
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full p-0 text-[10px] sm:text-xs flex items-center justify-center border-2 border-background"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </div>
        <span className="text-xs sm:text-sm font-medium max-w-[80px] sm:max-w-[120px] truncate hidden md:inline text-primary">
          {user?.user_metadata?.display_name || user?.email?.split("@")[0]}
        </span>
        <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
      </Button>
      {modalEverOpened && <UserProfileModal open={modalOpen} onOpenChange={setModalOpen} />}
    </>
  );
}
